/**
 * HotelProfileDirect — auto-opens the current hotel's profile as an editable
 * form. No list, no hotel selector.
 *
 * ADR-WF-1 v2.1: inside the Hotel Shell there is exactly one hotel — the one
 * selected by the HotelSwitcher. So `/master/hotel-profiles` must render the
 * detail form directly for `HotelContext.hotel_id`, never a list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { findModule } from "@/lib/masterConfig";
import { useHotelContext } from "@/contexts/HotelContext";
import { PageHeader, Card } from "@/components/primitives";
import HotelLogoUploader from "./HotelLogoUploader";
import RegCardTermsCard from "./RegCardTermsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Save, Loader2, Lock } from "lucide-react";

function FieldInput({ field, value, onChange }) {
  const val = value ?? (field.default !== undefined ? field.default : "");
  if (field.type === "textarea") return (
    <Textarea value={val || ""} onChange={(e) => onChange(e.target.value)} rows={3}
      className="bg-hocp-bgCard border-hocp-border" data-testid={`field-${field.key}`}/>
  );
  if (field.type === "select") return (
    <Select value={val || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-hocp-bgCard border-hocp-border" data-testid={`field-${field.key}`}>
        <SelectValue placeholder={`Select ${field.label}`}/>
      </SelectTrigger>
      <SelectContent>
        {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (field.type === "boolean") return (
    <div className="flex items-center gap-2">
      <Switch checked={!!val} onCheckedChange={onChange} data-testid={`field-${field.key}`}/>
      <span className="text-xs text-hocp-textMuted">{val ? "Yes" : "No"}</span>
    </div>
  );
  return (
    <Input
      type={field.type === "number" ? "number" : (field.type === "date" ? "date" : (field.type === "time" ? "time" : (field.type === "email" ? "email" : "text")))}
      value={val ?? ""}
      onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
      readOnly={!!field.readonly}
      disabled={!!field.readonly}
      className={`bg-hocp-bgCard border-hocp-border ${field.mono ? "mono" : ""} ${field.readonly ? "opacity-60 cursor-not-allowed text-hocp-textMuted" : ""}`}
      required={field.required}
      data-testid={`field-${field.key}`}
    />
  );
}

export default function HotelProfileDirect() {
  const cfg = findModule("hotel-profiles");
  const { ctx } = useHotelContext();
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const hotelId = ctx?.hotel_id;

  const load = useCallback(async () => {
    if (!hotelId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/master/hotel-profiles/?page=1&page_size=5&filters=hotel_id:${hotelId}`,
      );
      const item = (data.items || []).find((r) => r.hotel_id === hotelId) || (data.items || [])[0];
      if (!item) {
        setError(
          "No hotel profile found for the current hotel. Ask a Super Admin to (re)create this hotel from the Platform Console.",
        );
        setRecord(null);
        setForm({});
      } else {
        setRecord(item);
        setForm({ ...item });
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);

  const fieldGroups = useMemo(() => {
    const groups = {};
    (cfg?.fields || []).forEach((f) => {
      const g = f.group || "Details";
      (groups[g] = groups[g] || []).push(f);
    });
    return groups;
  }, [cfg]);

  const submit = async () => {
    if (!record) return;
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "created_at", "updated_at", "created_by", "updated_by", "is_deleted", "deleted_at", "hotel_id"].forEach((k) => delete payload[k]);
      await api.patch(`/master/hotel-profiles/${record.id}`, payload);
      toast.success("Hotel profile saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="text-hocp-textMuted text-sm">Module not found.</div>;

  return (
    <div data-testid="master-page-hotel-profiles">
      <PageHeader
        title={cfg.title}
        subtitle={ctx?.hotel_name ? `${ctx.hotel_name} · ${ctx.hotel_code || ""}` : cfg.subtitle}
        action={
          <Button
            onClick={submit}
            disabled={!record || saving || loading}
            className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]"
            data-testid="save-hotel-profile-button"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1.5"/>}
            Save changes
          </Button>
        }
      />

      <div
        className="flex items-start gap-3 rounded-lg border border-blue-300 dark:border-blue-400/40 bg-blue-50 dark:bg-blue-500/15 px-4 py-3 text-[13px] text-blue-900 dark:text-blue-100 mb-4"
        data-testid="hotel-profile-scope-banner"
      >
        <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-300 shrink-0" />
        <div className="font-medium">
          Editing the profile for <span className="mono">{ctx?.hotel_name || "current hotel"}</span>. The
          hotel record itself is managed by the platform Super Admin — use the Platform Console
          to create or delete hotels.
        </div>
      </div>

      {!loading && !hotelId && (
        <Card className="p-8 text-center text-hocp-textMuted text-[13px]" data-testid="hotel-profile-no-hotel">
          Select a hotel from the switcher above to view its profile.
        </Card>
      )}

      {loading && (
        <Card className="p-10 flex items-center justify-center gap-3 text-hocp-textMuted text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading hotel profile…
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6 text-red-400 text-[13px]" data-testid="hotel-profile-error">
          {error}
        </Card>
      )}

      {!loading && !error && record && (
        <>
        <HotelLogoUploader />
        <Card className="p-6">
          <div className="space-y-6">
            {Object.entries(fieldGroups).map(([gname, fields]) => (
              <div key={gname}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-3 pb-2 border-b border-hocp-border">
                  {gname}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields.map((f) => (
                    <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                      <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">
                        {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                        {f.readonly && <span className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-[10px] text-amber-400/80"><Lock className="w-2.5 h-2.5"/>Locked</span>}
                      </Label>
                      <div className="mt-1.5">
                        <FieldInput
                          field={f}
                          value={form[f.key]}
                          onChange={(v) => setForm({ ...form, [f.key]: v })}
                        />
                      </div>
                      {f.hint && <p className="mt-1 text-[11px] text-hocp-textMuted">{f.hint}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-hocp-border">
              <Switch
                checked={form.is_active ?? true}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="field-is_active"
              />
              <span className="text-xs text-hocp-textMuted">Active</span>
            </div>
          </div>
        </Card>
        <RegCardTermsCard hotelId={hotelId} />
        </>
      )}
    </div>
  );
}
